import { useState, useRef, useEffect } from "react";
import { Input, Checkbox, Dropdown, Button, Tag } from "antd";
import { SearchOutlined, FilterOutlined, CloseOutlined } from "@ant-design/icons";
import { labRoleMap } from "../constants/roles";

const LAB_OPTIONS = Object.keys(labRoleMap);
const SEARCH_FIELD_OPTIONS = [
  { label: "Name", value: "name" },
  { label: "Email", value: "email" },
];

const UserSearchBar = ({
  onSearchChange,        // ({ searchText, searchFields, selectedLabs, selectedAuthority, unassignedOnly }) => void
  authorities,           // string[] derived from users
}) => {
  const [searchText, setSearchText] = useState("");
  const [searchFields, setSearchFields] = useState(["name", "email"]);
  const [selectedLabs, setSelectedLabs] = useState([]);
  const [selectedAuthority, setSelectedAuthority] = useState(null);
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const inputRef = useRef(null);

  // Notify parent on any filter change
  useEffect(() => {
    onSearchChange({ searchText, searchFields, selectedLabs, selectedAuthority, unassignedOnly });
  }, [searchText, searchFields, selectedLabs, selectedAuthority, unassignedOnly]); // eslint-disable-line

  const activeFilterCount =
    selectedLabs.length +
    (selectedAuthority ? 1 : 0) +
    (unassignedOnly ? 1 : 0);

  const clearAll = () => {
    setSearchText("");
    setSelectedLabs([]);
    setSelectedAuthority(null);
    setUnassignedOnly(false);
    setSearchFields(["name", "email"]);
  };

  const filterDropdownContent = (
    <div style={styles.filterPanel}>
      <div style={styles.filterSection}>
        <p style={styles.filterLabel}>Search by</p>
        <Checkbox.Group
          options={SEARCH_FIELD_OPTIONS}
          value={searchFields}
          onChange={setSearchFields}
        />
      </div>

      <div style={styles.filterDivider} />

      <div style={styles.filterSection}>
        <p style={styles.filterLabel}>Lab</p>
        <div style={styles.labGrid}>
          {LAB_OPTIONS.map((lab) => (
            <Tag.CheckableTag
              key={lab}
              checked={selectedLabs.includes(lab)}
              onChange={(checked) =>
                setSelectedLabs((prev) =>
                  checked ? [...prev, lab] : prev.filter((l) => l !== lab)
                )
              }
              style={styles.labTag}
            >
              {lab}
            </Tag.CheckableTag>
          ))}
        </div>
      </div>

      <div style={styles.filterDivider} />

      <div style={styles.filterSection}>
        <p style={styles.filterLabel}>Authority</p>
        <div style={styles.labGrid}>
          {authorities.map((auth) => (
            <Tag.CheckableTag
              key={auth}
              checked={selectedAuthority === auth}
              onChange={(checked) => setSelectedAuthority(checked ? auth : null)}
              style={styles.labTag}
            >
              {auth}
            </Tag.CheckableTag>
          ))}
        </div>
      </div>

      <div style={styles.filterDivider} />

      <div style={styles.filterSection}>
        <Checkbox
          checked={unassignedOnly}
          onChange={(e) => setUnassignedOnly(e.target.checked)}
        >
          Show unassigned users only
        </Checkbox>
      </div>

      {activeFilterCount > 0 && (
        <>
          <div style={styles.filterDivider} />
          <Button
            size="small"
            type="link"
            danger
            icon={<CloseOutlined />}
            onClick={clearAll}
            style={{ padding: 0 }}
          >
            Clear all filters
          </Button>
        </>
      )}
    </div>
  );

  return (
    <div style={styles.wrapper}>
      <div style={{ ...styles.searchRow, width: expanded ? 380 : 220, transition: "width 0.25s ease" }}>
        <Input
          ref={inputRef}
          prefix={<SearchOutlined style={{ color: "#8c8c8c" }} />}
          placeholder="Search users..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onFocus={() => setExpanded(true)}
          onBlur={() => !searchText && setExpanded(false)}
          allowClear
          style={styles.searchInput}
          bordered={false}
        />
        <Dropdown
          open={filterOpen}
          onOpenChange={setFilterOpen}
          overlay={filterDropdownContent}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button
            icon={<FilterOutlined />}
            style={{
              ...styles.filterBtn,
              borderColor: activeFilterCount > 0 ? "#1890ff" : "#d9d9d9",
              color: activeFilterCount > 0 ? "#1890ff" : "inherit",
            }}
          >
            {activeFilterCount > 0 && (
              <span style={styles.filterBadge}>{activeFilterCount}</span>
            )}
          </Button>
        </Dropdown>
      </div>
    </div>
  );
};

const styles = {
  wrapper: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  searchRow: {
    display: "flex",
    alignItems: "center",
    border: "1px solid #d9d9d9",
    borderRadius: 6,
    overflow: "hidden",
    background: "#fff",
  },
  searchInput: {
    flex: 1,
    border: "none",
    boxShadow: "none",
  },
  filterBtn: {
    border: "none",
    borderLeft: "1px solid #d9d9d9",
    borderRadius: 0,
    height: 32,
    display: "flex",
    alignItems: "center",
    gap: 4,
    position: "relative",
  },
  filterBadge: {
    background: "#1890ff",
    color: "#fff",
    borderRadius: "50%",
    width: 16,
    height: 16,
    fontSize: 10,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: 2,
  },
  filterPanel: {
    background: "#fff",
    border: "1px solid #f0f0f0",
    borderRadius: 8,
    padding: "12px 16px",
    minWidth: 260,
    boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
  },
  filterSection: {
    paddingBottom: 8,
  },
  filterLabel: {
    margin: "0 0 6px",
    fontSize: 12,
    fontWeight: 600,
    color: "#8c8c8c",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  filterDivider: {
    borderTop: "1px solid #f0f0f0",
    margin: "8px 0",
  },
  labGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
  },
  labTag: {
    borderRadius: 4,
    cursor: "pointer",
    margin: 0,
  },
};

export default UserSearchBar;